import type { JSX } from "react";
import { cn } from "./cn";
import { Icon } from "./Icon";
import type { IconName } from "./Icon";

export interface TriggerCondition {
  /** Condition icon */
  readonly conditionIcon: IconName;
  /** Condition text (e.g. "If: No reply for 48h on Telegram") */
  readonly conditionText: string;
  /** Action icon */
  readonly actionIcon: IconName;
  /** Action text (e.g. "Then: Send gentle nudge") */
  readonly actionText: string;
}

export interface TriggerCardProps {
  /** Leading icon */
  readonly icon?: IconName;
  /** Icon color class */
  readonly iconColor?: string;
  /** Trigger title */
  readonly title: string;
  /** Whether trigger is active (shows ON badge) */
  readonly active?: boolean;
  /** If/Then condition row */
  readonly condition?: TriggerCondition;
  /** Click handler */
  readonly onClick?: () => void;
  /** Extra className */
  readonly className?: string;
}

/**
 * Trigger card with icon, title, ON badge, and If/Then condition.
 * Matches B1 Pencil design — reusable for automation triggers.
 */
export function TriggerCard({
  icon = "zap",
  iconColor = "text-content-secondary",
  title,
  active = false,
  condition,
  onClick,
  className,
}: TriggerCardProps): JSX.Element {
  const Component = onClick ? "button" : "div";
  return (
    <Component
      type={onClick ? "button" : undefined}
      className={cn(
        "flex flex-col gap-2 bg-surface-secondary rounded-[10px] px-4 py-3.5",
        "border border-edge",
        onClick && "cursor-pointer hover:bg-surface-hover transition-colors text-left w-full",
        className,
      )}
      onClick={onClick}
    >
      <div className="flex items-center gap-2 min-w-0">
        <span className={cn("shrink-0", iconColor)}>
          <Icon name={icon} size={16} />
        </span>
        <span className="flex-1 text-[13px] font-medium text-content truncate">{title}</span>
        {active ? (
          <span className="shrink-0 px-2 py-[3px] rounded-md text-[10px] font-bold uppercase bg-accent-subtle text-accent">
            ON
          </span>
        ) : null}
      </div>
      {condition ? (
        <div className="flex items-center gap-4 text-[11px] text-content-secondary">
          <span className="flex items-center gap-1.5">
            <Icon name={condition.conditionIcon} size={13} />
            <span>{condition.conditionText}</span>
          </span>
          <span className="text-content-tertiary">
            <Icon name="arrow-right" size={12} />
          </span>
          <span className="flex items-center gap-1.5">
            <Icon name={condition.actionIcon} size={13} />
            <span>{condition.actionText}</span>
          </span>
        </div>
      ) : null}
    </Component>
  );
}
